import { useState, useEffect, type KeyboardEvent, useMemo, useRef } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, InputLabel, TextField, IconButton, Divider } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { GenericOption } from './FilterMultiSelect';
import { AdvancedFilterDrawerProps, AssignmentGroupAlert, FilterState } from './types';
import { DEFAULT_FILTERS } from './constants';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import SearchableSelectDropdown from 'components/Dropdown/MultiFilterPanel/SearchableSelectDropdown';
import CommonDrawer from '../../Common/CommonDrawer';
import AddIcon from '@mui/icons-material/Add';
import {
  Checkbox,
  FormControlLabel,
  Popover
} from '@mui/material';
import { FILTER_RELATIONSHIP_FIELD_MAP, getCascadingFilterOptions, getDerivedAssignmentGroups, reconcileCascadingFilters } from '../hooks/useUnifiedAlerts';
const AdvancedFilterDrawer = ({
  open,
  onClose,
  onApply,
  onSaveDefault,
  onResetDefault,
  currentFilters,
  filterOptions,
  filterRelationshipRecords,
  lockedStatus,
  defaultStatus
}: AdvancedFilterDrawerProps) => {
  const theme = useTheme();
  const [filters, setFilters] = useState<FilterState>(currentFilters);
  const [moreFilterAnchor, setMoreFilterAnchor] = useState<null | HTMLElement>(null);
  const [selectedMoreFilters, setSelectedMoreFilters] = useState<string[]>([]);
  const assignmentGroupsAreDerivedRef = useRef(false);

  const isMiscellaneousSelected =
    filters?.sector?.some(
      item => item.value === 'Miscellaneous'
    ) ?? false;

  const customFilterFields = [
    { label: 'Created By', value: 'sys_created_by' },
    { label: 'Updated By', value: 'sys_updated_by' },
    { label: 'Category', value: 'category' },
    { label: 'Classification', value: 'classification' }
  ];

  // Sync filters when drawer opens with current applied filters
  useEffect(() => {
    if (open) {
      setFilters(currentFilters);
      assignmentGroupsAreDerivedRef.current = false;
      previousAssignmentGroupSourceRef.current = null;
    }
  }, [open, currentFilters]);

  const disabledOptionsMap: Record<string, string[]> = {
    assignment_group: [
      'ADVANCED ANALYTICS',
      'ADVANCED ANALYTICS DELIVERY (AAD) DATA SUPPORT',
      'ADVANCED ANALYTICS DELIVERY (AAD) UI SUPPORT',
      'ADVANCED ANALYTICS PLATFORM LICENSE DEPLOYMENT',
    ],
  };

const toOptions = (values: string[], key?: string): GenericOption[] => {
  return values?.map(v => {
    if (!v && ["service_offering", "cmdb_ci"].includes(key as string)) {
      const label = key === "service_offering" ? "SO" : "CI";
      return { label: `${label} not available`, value: v };
    }

    return {
      label: v,
      value: v
    };
  }).filter(
    option =>
      option.label !== null &&
      option.label !== undefined &&
      option.label !== '' &&
      option.value !== null &&
      option.value !== undefined &&
      option.value !== ''
  ) || [];
};

  const toAGOptions = (values: AssignmentGroupAlert[], key?: string): GenericOption[] => {
    return values?.map(v => {
      return { label: v.assignmentGroup, value: v.assignmentGroup, disabled: !v.pipelineAlert && !v.dataQualityAlert }
    }) || [];
  };

  const getSelectedValues = (items?: GenericOption[]) => {
    return Array.isArray(items) ? items.map(item => item.value) : [];
  };

  const getSelectedOptions = (options: GenericOption[], value: string[] | string | null) => {
    const selectedValues = Array.isArray(value) ? value : value ? [value] : [];

    return options.filter(option => !option.disabled && selectedValues.includes(option.value));
  };

  const miscellaneousSectorValues =
    filterOptions?.sectorDropdownValues?.Miscellaneous || [];

  useEffect(() => {
    if (!isMiscellaneousSelected) {
      setFilters(prev => {
        if (!prev.miscellaneousSector?.length) {
          return prev;
        }

        return {
          ...prev,
          miscellaneousSector: []
        };
      });

      return;
    }

    const options = miscellaneousSectorValues.map(v => ({
      label: v,
      value: v
    }));

    setFilters(prev => {
      if (prev.miscellaneousSector?.length > 0) {
        return prev;
      }

      return {
        ...prev,
        miscellaneousSector: options
      };
    });
  }, [
    isMiscellaneousSelected,
    miscellaneousSectorValues
  ]);

  const handleDomainChange = (newValue: GenericOption[]) => {
    const uniqueDomains = newValue.filter((item, index, self) =>
      index === self.findIndex((t) => t.value === item.value)
    );
    setFilters(prev => ({ ...prev, domains: uniqueDomains }));
  };

  const handleProgramChange = (newValue: GenericOption[]) => {
    const uniquePrograms = newValue.filter((item, index, self) =>
      index === self.findIndex((t) => t.value === item.value)
    );
    setFilters(prev => ({ ...prev, programs: uniquePrograms }));
  };

  const handleApply = () => {
    onApply(buildFiltersForSubmit());
    onClose();
  };

  const buildFiltersForSubmit = (): FilterState => {
    const sessionTimeRange = sessionStorage.getItem('unifiedAlertsDateRangeType') as FilterState['timeRange'] | null;
    const selectedTimeRange = sessionTimeRange || filters.timeRange;
    const startDate = sessionStorage.getItem('unifiedAlertsStartDate');
    const endDate = sessionStorage.getItem('unifiedAlertsEndDate');

    if (selectedTimeRange === 'custom' && startDate && endDate) {
      return {
        ...filters,
        timeRange: 'custom',
        startDate,
        endDate,
      };
    }

    return {
      ...filters,
      timeRange: selectedTimeRange,
      startDate: undefined,
      endDate: undefined,
    };
  };

  const handleSaveDefault = () => {
    onSaveDefault(buildFiltersForSubmit());
    onClose();
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleApply();
    }
  };

  const handleReset = () => {
    assignmentGroupsAreDerivedRef.current = false;
    previousAssignmentGroupSourceRef.current = null;
    setFilters(DEFAULT_FILTERS);
    onApply(DEFAULT_FILTERS);
  };

  const handleResetDefault = () => {
    assignmentGroupsAreDerivedRef.current = false;
    previousAssignmentGroupSourceRef.current = null;
    onResetDefault(DEFAULT_FILTERS);

    setFilters(DEFAULT_FILTERS);
    onApply(DEFAULT_FILTERS);

    onClose();
  }

  const handleMoreFilter = (event: React.MouseEvent<HTMLElement>) => {
    setMoreFilterAnchor(event.currentTarget);
  };

  const handleCloseMoreFilter = () => {
    setMoreFilterAnchor(null);
  };

  const handleMoreFilterChange = (filter: string) => {
    setSelectedMoreFilters(prev =>
      prev.includes(filter)
        ? prev.filter(item => item !== filter)
        : [...prev, filter]
    );
  };

  const isMoreFilterOpen = Boolean(moreFilterAnchor);

  const handleClose = () => {
    // Discard temp changes
    setFilters(currentFilters);
    assignmentGroupsAreDerivedRef.current = false;
    previousAssignmentGroupSourceRef.current = null;
    onClose();
  };

  const handleAddCustomFilter = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setFilters(prev => ({
      ...prev,
      additionalFilters: [...prev.additionalFilters, { id, field: '', value: '' }]
    }));
  };

  const handleUpdateCustomFilter = (id: string, changes: Partial<{ field: string; value: string }>) => {
    setFilters(prev => ({
      ...prev,
      additionalFilters: prev.additionalFilters.map((f) =>
        f.id === id ? { ...f, ...changes } : f
      )
    }));
  };

  const handleRemoveCustomFilter = (id: string) => {
    setFilters(prev => ({
      ...prev,
      additionalFilters: prev.additionalFilters.filter((f) => f.id !== id)
    }));
  };

const miscellaneousSectors =
  filterOptions?.sectorDropdownValues?.Miscellaneous || [];

const cascadingFilterOptions = useMemo(() => {
  return getCascadingFilterOptions(
    filters,
    filterRelationshipRecords,
    miscellaneousSectors,
    {
      ignoreDerivedAssignmentGroup:
        assignmentGroupsAreDerivedRef.current
    }
  );
}, [
  filters,
  filterRelationshipRecords,
  miscellaneousSectors
]);
  const previousCascadingFiltersRef = useRef<Record<string, string[]> | null>(null);
 const previousAssignmentGroupSourceRef = useRef<string | null>(null);

  // useEffect(() => {
  //   console.log('🔄 CASCADING FILTERS', {
  //     before: previousCascadingFiltersRef.current,
  //     after: cascadingFilterOptions,
  //   });

  //   previousCascadingFiltersRef.current = cascadingFilterOptions;
  // }, [cascadingFilterOptions]);

  useEffect(() => {
    if (!Object.keys(cascadingFilterOptions).length) {
      return;
    }

    const reconciledFilters = reconcileCascadingFilters(
      filters,
      cascadingFilterOptions
    );

    if (reconciledFilters !== filters) {
      // setFilters(reconciledFilters);
       setFilters({
          ...reconciledFilters,

          // Sector is independently controlled.
          // Do not allow cascading reconciliation to modify it.
          sector: filters.sector
      });
    }
  }, [
    cascadingFilterOptions,
    filters
  ]);


  const cascadingAssignmentGroups = useMemo(() => {
    const cascadingGroups = cascadingFilterOptions.assignmentGroup;

    // No relationship filters selected yet.
    // Use the original Assignment Group options.
    if (cascadingGroups === undefined) {
      return filterOptions.assignmentGroup || [];
    }

    const allowedGroups = new Set(cascadingGroups);

    return (filterOptions.assignmentGroup || []).filter(
      (group: AssignmentGroupAlert) =>
        allowedGroups.has(String(group.assignmentGroup))
    );
  }, [
    cascadingFilterOptions.assignmentGroup,
    filterOptions.assignmentGroup
  ]);

  const derivedAssignmentGroups = useMemo(() => {
      return getDerivedAssignmentGroups(
        filters,
        filterRelationshipRecords
      );
    }, [
      filters.vendor,
      filters.team,
      filters.subCapability,
      filters.technology,
      filters.assignmentGroupListName,
      filterRelationshipRecords
    ]);

    const assignmentGroupSourceSignature = useMemo(() => {
      const getValues = (
        options?: GenericOption[]
      ) =>
        (options || [])
          .map(option => String(option.value))
          .sort();

      return JSON.stringify({
        vendor: getValues(filters.vendor),
        team: getValues(filters.team),
        subCapability: getValues(filters.subCapability),
        technology: getValues(filters.technology),
        assignmentGroupListName: getValues(
          filters.assignmentGroupListName
        )
      });
    }, [
      filters.vendor,
      filters.team,
      filters.subCapability,
      filters.technology,
      filters.assignmentGroupListName
    ]);

    useEffect(() => {
      /*
      * Don't process the same Vendor / Team / AG List
      * combination again.
      */
      if (
        previousAssignmentGroupSourceRef.current ===
        assignmentGroupSourceSignature
      ) {
        return;
      }

      previousAssignmentGroupSourceRef.current =
        assignmentGroupSourceSignature;

      /*
      * --------------------------------------------------
      * CASE 1:
      * No Vendor / Team / AG List is selected anymore.
      * --------------------------------------------------
      *
      * If Assignment Group was previously populated
      * automatically, clear it.
      *
      * If Assignment Group was manually selected,
      * leave it alone.
      */
      if (!derivedAssignmentGroups.length) {
        if (assignmentGroupsAreDerivedRef.current) {
          assignmentGroupsAreDerivedRef.current = false;

          setFilters(prev => {
            if (!prev.assignmentGroup?.length) {
              return prev;
            }

            return {
              ...prev,
              assignmentGroup: []
            };
          });
        }

        return;
      }

      /*
      * --------------------------------------------------
      * CASE 2:
      * Vendor / Team / AG List has active selections.
      * --------------------------------------------------
      */

      const assignmentGroupOptions = toAGOptions(
        filterOptions.assignmentGroup || [],
        'assignment_group'
      );

      const nextAssignmentGroups = getSelectedOptions(
        assignmentGroupOptions,
        derivedAssignmentGroups
      );

      /*
      * This Assignment Group selection is generated
      * from Vendor / Team / AG List.
      */
      assignmentGroupsAreDerivedRef.current = true;

      setFilters(prev => {
        const previousValues = (prev.assignmentGroup || [])
          .map(option => String(option.value))
          .sort();

        const nextValues = nextAssignmentGroups
          .map(option => String(option.value))
          .sort();

        /*
        * Nothing actually changed.
        */
        if (
          previousValues.length === nextValues.length &&
          previousValues.every(
            (value, index) => value === nextValues[index]
          )
        ) {
          return prev;
        }

        return {
          ...prev,
          assignmentGroup: nextAssignmentGroups
        };
      });
    }, [
      assignmentGroupSourceSignature,
      derivedAssignmentGroups,
      filterOptions.assignmentGroup
    ]);

  const getCascadingOptions = (
    key: keyof typeof FILTER_RELATIONSHIP_FIELD_MAP,
    fallback: string[]
  ) => {
    return cascadingFilterOptions[key] ?? fallback;
  };

  return (
    <CommonDrawer
      open={open}
      onClose={handleClose}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#FFFFFF' }}
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              color: theme.palette.filterPanel.headerTitle,
            }}
          >
            Advance Filters
          </Typography>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>

              <Button
                  variant="text"
                  onClick={handleMoreFilter}
                  startIcon={<AddIcon sx={{ fontSize: '1rem' }} />}
                  sx={{
                    color: `${theme.palette.filterPanel.clearButtonText} !important`,
                    textTransform: 'uppercase',
                    fontWeight: 500,
                    fontSize: '14px'
                  }}
                >
                  More Filters
              </Button>

              <IconButton size="small" onClick={handleClose} sx={{ '&&': { p: 0.5 } }}>
                <CloseIcon fontSize="small" sx={{ color: theme.palette.filterPanel.headerTitle }} />
              </IconButton>

          </Box>
         
        </Box>

        <Popover
          open={isMoreFilterOpen}
          anchorEl={moreFilterAnchor}
          onClose={handleCloseMoreFilter}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              width: 220,
              borderRadius: 1.5,
              boxShadow: theme.shadows[4],
              p: 1,
            },
          }}
        >
          <Box>
            {[
              { key: 'bu', label: 'Business Unit' },
              { key: 'scap', label: 'Sub Capability' },
              { key: 'tech', label: 'Technology' },
              { key: 'agList', label: 'Assignment Group List' },
              { key: 'status', label: 'Status' },
              { key: 'severe', label: 'Severity' },
              { key: 'prior', label: 'Priority' },
            ].map(filter => (
              <FormControlLabel
                key={filter.key}
                control={
                  <Checkbox
                    size="small"
                    checked={selectedMoreFilters.includes(filter.key)}
                    onChange={() => handleMoreFilterChange(filter.key)}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                    }}
                  >
                    {filter.label}
                  </Typography>
                }
                sx={{
                  display: 'flex',
                  width: '100%',
                  mx: 0,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              />
            ))}
          </Box>
        </Popover>

        <Divider sx={{ borderColor: theme.palette.filterPanel.headerTitle }} />

        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          marginTop: '10px'
        }}>
          <Button
            variant="text"
            onClick={handleResetDefault}
            startIcon={<SyncIcon sx={{ fontSize: '1rem' }} />}
            sx={{
              color: `${theme.palette.filterPanel.clearButtonText} !important`,
              textTransform: 'uppercase',
              fontWeight: 500,
              fontSize: '14px'
            }}
          >
            RESET DEFAULT FILTERS
          </Button>
          <Button
            variant="outlined"
            onClick={handleSaveDefault}
            sx={{
              color: `${theme.palette.filterPanel.clearButtonText} !important`,
              borderColor: `${theme.palette.filterPanel.clearButtonText} !important`,
              textTransform: 'uppercase',
              fontWeight: 500,
              fontSize: '14px',
              '&:hover': {
                borderColor: `${theme.palette.filterPanel.clearButtonText} !important`,
              }
            }}
          >
            SAVE AS DEFAULT
          </Button>
        </Box>

        {/* Body */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, backgroundColor: '#FFFFFF' }}>

          {/* Department */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Department"
              placeholder="Select Department..."
              options={toOptions(
                getCascadingOptions(
                  'department',
                  filterOptions.department || []
                )
              )}
              multiple
              value={getSelectedValues(filters.department)}
              onChange={(next) =>
                setFilters(prev => ({
                  ...prev,
                  department: getSelectedOptions(
                    toOptions(
                      getCascadingOptions(
                        'department',
                        filterOptions.department || []
                      )
                    ),
                    next
                  )
                }))
              }
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Capability Category */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Capability"
              placeholder="Select Capability..."
              options={toOptions(
                getCascadingOptions(
                  'capability',
                  filterOptions.capability || []
                )
              )}
              multiple
              value={getSelectedValues(filters.capability)}
              onChange={(next) =>
                setFilters(prev => ({
                  ...prev,
                  capability: getSelectedOptions(
                    toOptions(
                      getCascadingOptions(
                        'capability',
                        filterOptions.capability || []
                      )
                    ),
                    next
                  )
                }))
              }
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Sector */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Sector"
              placeholder="Select sectors..."
              options={toOptions(
                  filterOptions?.sector || []
                )}
              multiple
              value={getSelectedValues(filters.sector)}
              onChange={(next) =>
                setFilters(prev => ({
                  ...prev,
                  sector: getSelectedOptions(
                    toOptions(
                      filterOptions.sector || []
                    ),
                    next
                  )
                }))
              }
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

         {isMiscellaneousSelected && (
            <Box sx={{ mb: 2 }}>
              <SearchableSelectDropdown
                label="Miscellaneous Sector"
                placeholder="Select Miscellaneous Sector..."
                options={toOptions(
                  filterOptions?.sectorDropdownValues?.Miscellaneous || []
                )}
                multiple
                value={getSelectedValues(
                  filters.miscellaneousSector || []
                )}
                onChange={(next) =>
                  setFilters(prev => ({
                    ...prev,
                    miscellaneousSector: getSelectedOptions(
                      toOptions(
                        filterOptions?.sectorDropdownValues?.Miscellaneous || []
                      ),
                      next
                    )
                  }))
                }
                showSearch
                showAllOption
                chipDisplay
              />
            </Box>
          )}

          {/* Domains */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Domains"
              placeholder="Select domains..."
             options={toOptions(
                getCascadingOptions(
                  'domains',
                  filterOptions.domains || []
                )
              )}
              multiple
              value={getSelectedValues(filters.domains)}
              onChange={(next) =>
                handleDomainChange(
                  getSelectedOptions(
                    toOptions(
                      getCascadingOptions(
                        'domains',
                        filterOptions.domains || []
                      )
                    ),
                    next
                  )
                )
              }
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Programs */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Programs"
              placeholder="Select programs..."
              options={toOptions(
                getCascadingOptions(
                  'programs',
                  filterOptions.programs || []
                )
              )}
              multiple
              value={getSelectedValues(filters.programs)}
             onChange={(next) =>
                handleProgramChange(
                  getSelectedOptions(
                    toOptions(
                      getCascadingOptions(
                        'programs',
                        filterOptions.programs || []
                      )
                    ),
                    next
                  )
                )
              }
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Vendor */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Vendor"
              placeholder="Select Vendor..."
              options={toOptions(
                getCascadingOptions(
                  'vendor',
                  filterOptions.vendor || []
                )
              )}
              multiple
              value={getSelectedValues(filters.vendor)}
              onChange={(next) =>
                setFilters(prev => ({
                  ...prev,
                  vendor: getSelectedOptions(
                    toOptions(
                      getCascadingOptions(
                        'vendor',
                        filterOptions.vendor || []
                      )
                    ),
                    next
                  )
                }))
              }
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Team */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Team"
              placeholder="Select Team..."
              options={toOptions(
                getCascadingOptions(
                  'team',
                  filterOptions.team || []
                )
              )}
              multiple
              value={getSelectedValues(filters.team)}
              onChange={(next) =>
                setFilters(prev => ({
                  ...prev,
                  team: getSelectedOptions(
                    toOptions(
                      getCascadingOptions(
                        'team',
                        filterOptions.team || []
                      )
                    ),
                    next
                  )
                }))
              }
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Assignment Group */}
          <Box sx={{ mb: 3 }}>
              <SearchableSelectDropdown
                label="Assignment Group"
                placeholder="Select assignment groups..."
                options={toAGOptions(
                  cascadingAssignmentGroups,
                  'assignment_group'
                )}
                multiple
                value={getSelectedValues(filters.assignmentGroup)}
                onChange={(next) => {
                  assignmentGroupsAreDerivedRef.current = false;

                  setFilters(prev => ({
                    ...prev,
                    assignmentGroup: getSelectedOptions(
                      toAGOptions(
                        cascadingAssignmentGroups,
                        'assignment_group'
                      ),
                      next
                    )
                  }));
                }}
                showSearch
                showAllOption
                chipDisplay
              />
          </Box>

          {/* Application */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Configuration Item"
              placeholder="Select configuration items..."
              options={toOptions(filterOptions.cmdbCi || [], "cmdb_ci")}
              multiple
              value={getSelectedValues(filters.cmdb_ci)}
              onChange={(next) => setFilters(prev => ({ ...prev, cmdb_ci: getSelectedOptions(toOptions(filterOptions.cmdbCi || [], "cmdb_ci"), next) }))}
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Source */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Source"
              placeholder="Select sources..."
              options={toOptions(filterOptions.sources || [])}
              multiple
              value={getSelectedValues(filters.sources)}
              onChange={(next) => setFilters(prev => ({ ...prev, sources: getSelectedOptions(toOptions(filterOptions.sources || []), next) }))}
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Alert Category */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="Alert Category"
              placeholder="Select alert categories..."
              options={toOptions(filterOptions.alertCategory || ['ALL', 'Pipeline', 'Data Quality'])}
              multiple
              value={getSelectedValues(filters.alertCategory)}
              onChange={(next) => setFilters(prev => ({ ...prev, alertCategory: getSelectedOptions(toOptions(filterOptions.alertCategory || ['ALL', 'Pipeline', 'Data Quality']), next) }))}
              showSearch
              showAllOption
              chipDisplay
            />
          </Box>

          {/* Business Units */}
          {selectedMoreFilters.includes('bu') && (
            <Box sx={{ mb: 2 }}>
              <SearchableSelectDropdown
                label="Business Units"
                placeholder="Select business units..."
                options={toOptions(
                  getCascadingOptions(
                    'businessUnits',
                    filterOptions.businessUnits || []
                  )
                )}
                multiple
                value={getSelectedValues(filters.businessUnits)}
                onChange={(next) =>
                  setFilters(prev => ({
                    ...prev,
                    businessUnits: getSelectedOptions(
                      toOptions(
                        getCascadingOptions(
                          'businessUnits',
                          filterOptions.businessUnits || []
                        )
                      ),
                      next
                    )
                  }))
                }
                showSearch
                showAllOption
                chipDisplay
              />
            </Box>
          )}

           {/* Sub Capability */}
          {selectedMoreFilters.includes('scap') && (
            <Box sx={{ mb: 2 }}>
              <SearchableSelectDropdown
                label="Sub Capability"
                placeholder="Select Sub Capability..."
                options={toOptions(
                  getCascadingOptions(
                    'subCapability',
                    filterOptions.subCapability || []
                  )
                )}
                multiple
                value={getSelectedValues(filters.subCapability)}
                onChange={(next) =>
                  setFilters(prev => ({
                    ...prev,
                    subCapability: getSelectedOptions(
                      toOptions(
                        getCascadingOptions(
                          'subCapability',
                          filterOptions.subCapability || []
                        )
                      ),
                      next
                    )
                  }))
                }
                showSearch
                showAllOption
                chipDisplay
              />
            </Box>
          )}

          {/* Technology */}
           {selectedMoreFilters.includes('tech') && (
            <Box sx={{ mb: 2 }}>
              <SearchableSelectDropdown
                label="Technology"
                placeholder="Select technology..."
                options={toOptions(
                  getCascadingOptions(
                    'technology',
                    filterOptions.technology || []
                  )
                )}
                multiple
                value={getSelectedValues(filters.technology)}
                onChange={(next) =>
                  setFilters(prev => ({
                    ...prev,
                    technology: getSelectedOptions(
                      toOptions(
                        getCascadingOptions(
                          'technology',
                          filterOptions.technology || []
                        )
                      ),
                      next
                    )
                  }))
                }
                showSearch
                showAllOption
                chipDisplay
              />
            </Box>
          )}

          {selectedMoreFilters.includes('agList') && (
            <Box sx={{ mb: 3 }}>
            <SearchableSelectDropdown
              label="Assignment Group List"
              placeholder="Select assignment groups list..."
              options={toOptions(
                getCascadingOptions(
                  'assignmentGroupListName',
                  filterOptions.assignment_group_list || []
                )
              )}
              multiple
              value={getSelectedValues(filters.assignmentGroupListName)}
              onChange={(next) => {
                const listOptions = getSelectedOptions(
                  toOptions(
                    getCascadingOptions(
                      'assignmentGroupListName',
                      filterOptions.assignment_group_list || []
                    )
                  ),
                  next
                );

                setFilters(prev => ({
                  ...prev,
                  assignmentGroupListName: listOptions
                }));
              }}
              showSearch
              showAllOption
              chipDisplay
            />
            </Box>
           )}

          {/* Status: fixed and read-only while a status-specific tile (Closed) is active */}
          {(selectedMoreFilters.includes('status') || lockedStatus || defaultStatus) && (
            <Box sx={{ mb: 2 }}>
              <SearchableSelectDropdown
                label="Status"
                placeholder="Select statuses..."
                options={lockedStatus ? toOptions([lockedStatus]) : toOptions(filterOptions.status || [])}
                multiple
                value={lockedStatus ? [lockedStatus] : getSelectedValues(filters.status)}
                onChange={(next) => {
                  if (lockedStatus) return;
                  setFilters(prev => ({ ...prev, status: getSelectedOptions(toOptions(filterOptions.status || []), next) }));
                }}
                disabled={Boolean(lockedStatus)}
                showSearch
                showAllOption
                chipDisplay
              />
            </Box>
          )}

          {/* D&AI */}
          <Box sx={{ mb: 2 }}>
            <SearchableSelectDropdown
              label="D&AI"
              placeholder="Select D&AI..."
              options={toOptions(['Yes', 'No'])}
              multiple
              value={getSelectedValues(filters.isDandAIAG)}
              onChange={(next) => {
                const nextArr = Array.isArray(next) ? next : next ? [next] : [];
                const singleVal = nextArr.length > 0 ? [nextArr[nextArr.length - 1]] : [];
                setFilters(prev => ({
                  ...prev,
                  isDandAIAG: getSelectedOptions(toOptions(['Yes', 'No']), singleVal)
                }));
              }}
              showSearch={false}
              showAllOption={false}
              chipDisplay
            />
          </Box>

          {/* Severity */}
          {selectedMoreFilters.includes('severe') && (
            <Box sx={{ mb: 2 }}>
              <SearchableSelectDropdown
                label="Severity"
                placeholder="Select severities..."
                options={toOptions(filterOptions.severity || [])}
                multiple
                value={getSelectedValues(filters.severity)}
                onChange={(next) => setFilters(prev => ({ ...prev, severity: getSelectedOptions(toOptions(filterOptions.severity || []), next) }))}
                showSearch
                showAllOption
                chipDisplay
              />
            </Box>
          )}

          {/* Priority */}
          {selectedMoreFilters.includes('prior') && (
              <Box sx={{ mb: 2 }}>
                <SearchableSelectDropdown
                  label="Priority"
                  placeholder="Select priorities..."
                  options={toOptions(filterOptions.priority || [])}
                  multiple
                  value={getSelectedValues(filters.priority)}
                  onChange={(next) => setFilters(prev => ({ ...prev, priority: getSelectedOptions(toOptions(filterOptions.priority || []), next) }))}
                  showSearch
                  showAllOption
                  chipDisplay
                />
              </Box>
          )}

          {/* Additional Filters */}
          <Box sx={{ py: 2, backgroundColor: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.filterPanel.headerTitle }}>
                Additional Filters
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddCustomFilter}
                sx={{ textTransform: 'uppercase', fontWeight: 500, fontSize: '0.9rem' }}
              >
                + Add Filter
              </Button>
            </Box>

            {filters.additionalFilters.map((filter) => (
              <Box
                key={filter.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: 1,
                  alignItems: 'center',
                  mb: 1
                }}
              >
                <FormControl fullWidth>
                  <InputLabel
                    sx={{
                      color: '#1b1d1fff !important',
                      fontWeight: 400,
                      backgroundColor: 'white',
                      paddingX: '4px',
                      marginLeft: '-2px',
                      "&.MuiInputLabel-root": {
                        color: '#333333 !important'
                      },
                      '&.Mui-focused': {
                        color: '#333333 !important',
                        backgroundColor: 'white'
                      },
                      '&.MuiInputLabel-shrink': {
                        color: '#333333 !important',
                        backgroundColor: 'white',
                        paddingX: '4px'
                      }
                    }}
                  >
                    Field
                  </InputLabel>
                  <Select
                    value={filter.field}
                    label="Select Field"
                    onChange={(e) => handleUpdateCustomFilter(filter.id, { field: e.target.value })}
                    IconComponent={KeyboardArrowDownIcon}
                    sx={{
                      height: '44px',
                      backgroundColor: `${theme.palette.filterDropdown.fieldBackground} !important`,
                      '& .MuiSelect-select': {
                        color: 'text.primary',
                        paddingTop: '10px',
                        paddingBottom: '10px'
                      },
                      '& .MuiSelect-icon': {
                        color: `${theme.palette.filterDropdown.fieldIcon} !important`
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: `${theme.palette.filterDropdown.panelBackground} !important`,
                          color: `${theme.palette.filterDropdown.label} !important`,
                          '& .MuiMenu-list': {
                            backgroundColor: `${theme.palette.moreOptionDropdown.background} !important`,
                          },
                          '& .MuiMenuItem-root': {
                            backgroundColor: `${theme.palette.filterDropdown.panelBackground} !important`,
                            color: `${theme.palette.filterDropdown.label} !important`,
                            '&:hover': {
                              backgroundColor: `${theme.palette.filterDropdown.optionHoverBackground} !important`,
                              color: `${theme.palette.filterDropdown.label} !important`,
                            },
                            '&.Mui-selected': {
                              backgroundColor: `${theme.palette.filterDropdown.optionHoverBackground} !important`,
                              color: `${theme.palette.filterDropdown.label} !important`,
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: `${theme.palette.filterDropdown.optionHoverBackground} !important`,
                              color: `${theme.palette.filterDropdown.label} !important`,
                            },
                          },
                        },
                      },
                    }}
                  >
                    {customFilterFields.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Value"
                  value={filter.value}
                  onChange={(e) => handleUpdateCustomFilter(filter.id, { value: e.target.value })}
                  fullWidth
                  sx={{
                    color: 'black !important',
                    backgroundColor: `${theme.palette.filterDropdown.fieldBackground} !important`,
                    '& .MuiInputBase-root': { height: '44px' },
                  }}
                  InputProps={{
                    sx: {
                      '& .MuiOutlinedInput-input': { color: `${theme.palette.text.primary} !important` }
                    }
                  }}
                  InputLabelProps={{
                    sx: {
                      color: `${theme.palette.text.primary} !important`,
                      fontWeight: 400,
                      backgroundColor: 'transparent',
                      paddingX: '4px',
                      marginLeft: '-2px',
                      "&.MuiInputLabel-root": {
                        color: '#333333 !important'
                      },
                      '&.Mui-focused': {
                        color: `${theme.palette.text.primary} !important`,
                        backgroundColor: 'white'
                      },
                      '&.MuiInputLabel-shrink': {
                        color: `${theme.palette.text.primary} !important`,
                        backgroundColor: 'white',
                        paddingX: '4px'
                      }
                    }
                  }}
                />

                <IconButton
                  onClick={() => handleRemoveCustomFilter(filter.id)}
                  sx={{
                    width: 35,
                    height: 35,
                    border: `1px solid ${theme.palette.inputNew.removeBorder} !important`,
                    borderRadius: '4px',
                    color: `${theme.palette.inputNew.removeIcon} !important`,
                    backgroundColor: '#FFFFFF !important',
                    '&:hover': {
                      backgroundColor: `${theme.palette.inputNew.removeHoverBackground} !important`,
                      border: `1px solid ${theme.palette.inputNew.removeBorder} !important`,
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: `${theme.palette.inputNew.removeIcon} !important` }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 2,
            gap: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            bgcolor: theme.palette.filterPanel.footerBackground,
          }}
        >
          <Button
            variant="text"
            onClick={handleReset}
            sx={{
              '&&': {
                color: `${theme.palette.filterPanel.clearButtonText} !important`,
                fontWeight: 500,
                textTransform: 'uppercase',
                fontSize: 14,
                p: 0,
                minWidth: 'unset',
              },
            }}
          >
            Clear Filters
          </Button>

          <Button
            variant="contained"
            onClick={handleApply}
            sx={{
              '&&': {
                bgcolor: `${theme.palette.filterPanel.applyButtonBackground} !important`,
                '&:hover': {
                  bgcolor: `${theme.palette.filterPanel.applyButtonHoverBackground} !important`,
                },
                fontWeight: 500,
                fontSize: 14,
                textTransform: 'uppercase',
                borderRadius: '4px',
                px: 3,
                py: 1,
                minHeight: 36,
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </CommonDrawer>
  );
};

export default AdvancedFilterDrawer;